import React, { useState } from 'react';
import { X, Plus, AlertCircle, CheckCircle } from 'lucide-react';
import { stcBinTypeService, CreateStcBinTypeRequest } from '../../../../../services/stcBinTypeService';
import { useAuth } from '../../../../../contexts/AuthContext';
import { useTranslation } from '../../../../../contexts/I18nProvider';

interface MasterDataLookups {
    binGroups: Map<number, { binGrpNo: number; description1?: string }>;
    priorities: Map<number, { priorityNo: number; description1?: string }>;
    movementTypes: Map<number, { movementTypeNo: number; description1?: string }>;
}

interface BinTypeCreateModalProps {
    isOpen: boolean;
    onClose: () => void;
    onSuccess: () => void;
    masterData: MasterDataLookups | null;
}

export const BinTypeCreateModal: React.FC<BinTypeCreateModalProps> = ({
    isOpen,
    onClose,
    onSuccess,
    masterData
}) => {
    const { user } = useAuth();
    const { t } = useTranslation();

    const [formData, setFormData] = useState<CreateStcBinTypeRequest>({
        companyNo: user?.companyNo || '001',
        binTypeNo: 1,
        description1: '',
        binGrpNo: undefined,
        pickPriorityNo: 1,
        putAwayPriorityNo: 1,
        pickCustomOrder: undefined,
        putAwayCustomOrder: undefined,
        movementTypeNo: undefined,
        reservationAllowed: true,
        negativeReservationAllowed: false,
        autoReplenishment: false,
        pickableYesNo: true,
        sharedBinYesNo: false,
        sharedBinMaxQuantity: undefined,
        sharedBinPreferEmptyBin: false,
        replenishMinLevel: undefined,
        replenishMaxLevel: undefined,
        replenishMaxBins: undefined,
        replenishFixedBins: false,
        replenishMaxBinsSource: undefined
    });
    const [isSaving, setIsSaving] = useState(false);
    const [error, setError] = useState('');
    const [showSuccess, setShowSuccess] = useState(false);

    const binGroups = masterData ? Array.from(masterData.binGroups.values()).sort((a, b) => a.binGrpNo - b.binGrpNo) : [];
    const priorities = masterData ? Array.from(masterData.priorities.values()).sort((a, b) => a.priorityNo - b.priorityNo) : [];
    const movementTypes = masterData ? Array.from(masterData.movementTypes.values()).sort((a, b) => a.movementTypeNo - b.movementTypeNo) : [];

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!formData.binTypeNo || formData.binTypeNo < 1) {
            setError(t('manager.modules.adminBins.binType.errors.typeNoRequired', {}, 'Lokasjonstype nr. er obligatorisk'));
            return;
        }

        try {
            setIsSaving(true);
            setError('');
            await stcBinTypeService.create(formData);

            setShowSuccess(true);
            setTimeout(() => {
                setShowSuccess(false);
                onSuccess();
            }, 1200);
        } catch (err: any) {
            setError(err.message || t('manager.modules.adminBins.binType.errors.createFailed', {}, 'Feil ved opprettelse av lokasjonstype'));
        } finally {
            setIsSaving(false);
        }
    };

    const updateField = <K extends keyof CreateStcBinTypeRequest>(key: K, value: CreateStcBinTypeRequest[K]) => {
        setFormData(prev => ({ ...prev, [key]: value }));
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-50 overflow-hidden">
            <div className="absolute inset-0 bg-black bg-opacity-50 transition-opacity" onClick={onClose} />

            <div className="absolute inset-0 flex items-center justify-center p-4">
                <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
                    {/* Header */}
                    <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 bg-gradient-to-r from-green-600 to-emerald-600 rounded-t-xl">
                        <div>
                            <h2 className="text-lg font-semibold text-white">{t('manager.modules.adminBins.binType.create.title', {}, 'Ny lokasjonstype')}</h2>
                            <p className="text-sm text-green-100">{t('manager.modules.adminBins.binType.create.subtitle', {}, 'Opprett en ny lokasjonstype')}</p>
                        </div>
                        <button onClick={onClose} className="text-white/80 hover:text-white transition-colors">
                            <X className="w-5 h-5" />
                        </button>
                    </div>

                    {/* Content */}
                    <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-6 space-y-5">
                        {error && (
                            <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                                <div className="flex items-start gap-2">
                                    <AlertCircle className="w-4 h-4 text-red-600 mt-0.5 flex-shrink-0" />
                                    <p className="text-sm text-red-800">{error}</p>
                                </div>
                            </div>
                        )}

                        {showSuccess && (
                            <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                                <div className="flex items-center gap-2">
                                    <CheckCircle className="w-4 h-4 text-green-600" />
                                    <p className="text-sm text-green-800 font-medium">{t('manager.modules.adminBins.binType.create.success', {}, 'Lokasjonstype opprettet!')}</p>
                                </div>
                            </div>
                        )}

                        {/* Grunndata */}
                        <fieldset className="border border-gray-200 rounded-lg p-4">
                            <legend className="text-sm font-semibold text-gray-700 px-2">{t('manager.modules.adminBins.common.masterData', {}, 'Grunndata')}</legend>
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-xs font-medium text-gray-700 mb-1">
                                        {t('manager.modules.adminBins.binType.fields.typeNoFull', {}, 'Lokasjonstype nr.')} <span className="text-red-500">*</span>
                                    </label>
                                    <input
                                        type="number"
                                        value={formData.binTypeNo}
                                        onChange={(e) => updateField('binTypeNo', parseInt(e.target.value) || 1)}
                                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                                        required
                                        min="1"
                                        autoFocus
                                    />
                                </div>
                                <div>
                                    <label className="block text-xs font-medium text-gray-700 mb-1">{t('common.fields.description', {}, 'Beskrivelse')}</label>
                                    <input
                                        type="text"
                                        value={formData.description1 || ''}
                                        onChange={(e) => updateField('description1', e.target.value)}
                                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                                        placeholder={t('manager.modules.adminBins.binType.create.descriptionPlaceholder', {}, 'Beskrivelse av lokasjonstypen')}
                                    />
                                </div>
                                <div>
                                    <label className="block text-xs font-medium text-gray-700 mb-1">{t('manager.modules.adminBins.binType.fields.binGroup', {}, 'Lokasjonsgruppe')}</label>
                                    <select
                                        value={formData.binGrpNo ?? ''}
                                        onChange={(e) => updateField('binGrpNo', e.target.value ? parseInt(e.target.value) : undefined)}
                                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent bg-white"
                                    >
                                        <option value="">{t('manager.modules.adminBins.binType.selectGroup', {}, '-- Velg gruppe --')}</option>
                                        {binGroups.map(g => (
                                            <option key={g.binGrpNo} value={g.binGrpNo}>
                                                {g.binGrpNo} - {g.description1}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                                <div>
                                    <label className="block text-xs font-medium text-gray-700 mb-1">{t('manager.modules.adminBins.binType.fields.movementType', {}, 'Bevegelseskode')}</label>
                                    <select
                                        value={formData.movementTypeNo ?? ''}
                                        onChange={(e) => updateField('movementTypeNo', e.target.value ? parseInt(e.target.value) : undefined)}
                                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent bg-white"
                                    >
                                        <option value="">{t('manager.modules.adminBins.binType.selectMovementType', {}, '-- Velg bevegelseskode --')}</option>
                                        {movementTypes.map(m => (
                                            <option key={m.movementTypeNo} value={m.movementTypeNo}>
                                                {m.movementTypeNo} - {m.description1}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            </div>
                        </fieldset>

                        {/* Prioritet */}
                        <fieldset className="border border-gray-200 rounded-lg p-4">
                            <legend className="text-sm font-semibold text-gray-700 px-2">{t('common.fields.priority', {}, 'Prioritet')}</legend>
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-xs font-medium text-gray-700 mb-1">{t('manager.modules.adminBins.binType.fields.pickPriority', {}, 'Plukkprioritet')}</label>
                                    <select
                                        value={formData.pickPriorityNo}
                                        onChange={(e) => updateField('pickPriorityNo', parseInt(e.target.value) || 1)}
                                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent bg-white"
                                    >
                                        {priorities.map(p => (
                                            <option key={`pick-${p.priorityNo}`} value={p.priorityNo}>
                                                {p.priorityNo} - {p.description1}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                                <div>
                                    <label className="block text-xs font-medium text-gray-700 mb-1">{t('manager.modules.adminBins.binType.fields.putAwayPriority', {}, 'Plasseringsprioritet')}</label>
                                    <select
                                        value={formData.putAwayPriorityNo}
                                        onChange={(e) => updateField('putAwayPriorityNo', parseInt(e.target.value) || 1)}
                                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent bg-white"
                                    >
                                        {priorities.map(p => (
                                            <option key={`put-${p.priorityNo}`} value={p.priorityNo}>
                                                {p.priorityNo} - {p.description1}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                                <div>
                                    <label className="block text-xs font-medium text-gray-700 mb-1">{t('manager.modules.adminBins.binType.fields.pickOrder', {}, 'Plukkrekkefølge')}</label>
                                    <input
                                        type="number"
                                        value={formData.pickCustomOrder ?? ''}
                                        onChange={(e) => updateField('pickCustomOrder', e.target.value ? parseInt(e.target.value) : undefined)}
                                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                                        placeholder="0"
                                    />
                                </div>
                                <div>
                                    <label className="block text-xs font-medium text-gray-700 mb-1">{t('manager.modules.adminBins.binType.fields.putAwayOrder', {}, 'Innlagringsrekkefølge')}</label>
                                    <input
                                        type="number"
                                        value={formData.putAwayCustomOrder ?? ''}
                                        onChange={(e) => updateField('putAwayCustomOrder', e.target.value ? parseInt(e.target.value) : undefined)}
                                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                                        placeholder="0"
                                    />
                                </div>
                            </div>
                        </fieldset>

                        {/* Innstillinger */}
                        <fieldset className="border border-gray-200 rounded-lg p-4">
                            <legend className="text-sm font-semibold text-gray-700 px-2">{t('manager.modules.adminBins.common.settings', {}, 'Innstillinger')}</legend>
                            <div className="grid grid-cols-2 gap-x-4 gap-y-3">
                                <label className="flex items-center gap-2 cursor-pointer">
                                    <input
                                        type="checkbox"
                                        checked={formData.pickableYesNo}
                                        onChange={(e) => updateField('pickableYesNo', e.target.checked)}
                                        className="w-4 h-4 text-green-600 border-gray-300 rounded focus:ring-green-500"
                                    />
                                    <span className="text-sm text-gray-700">{t('manager.modules.adminBins.binType.fields.pickable', {}, 'Plukkbar')}</span>
                                </label>
                                <label className="flex items-center gap-2 cursor-pointer">
                                    <input
                                        type="checkbox"
                                        checked={formData.reservationAllowed}
                                        onChange={(e) => updateField('reservationAllowed', e.target.checked)}
                                        className="w-4 h-4 text-green-600 border-gray-300 rounded focus:ring-green-500"
                                    />
                                    <span className="text-sm text-gray-700">{t('manager.modules.adminBins.binType.fields.reservationAllowed', {}, 'Reservasjon tillatt')}</span>
                                </label>
                                <label className="flex items-center gap-2 cursor-pointer">
                                    <input
                                        type="checkbox"
                                        checked={formData.negativeReservationAllowed}
                                        onChange={(e) => updateField('negativeReservationAllowed', e.target.checked)}
                                        className="w-4 h-4 text-green-600 border-gray-300 rounded focus:ring-green-500"
                                    />
                                    <span className="text-sm text-gray-700">{t('manager.modules.adminBins.binType.create.negativeStockAllowed', {}, 'Negativ beholdning tillatt')}</span>
                                </label>
                                <label className="flex items-center gap-2 cursor-pointer">
                                    <input
                                        type="checkbox"
                                        checked={formData.autoReplenishment}
                                        onChange={(e) => updateField('autoReplenishment', e.target.checked)}
                                        className="w-4 h-4 text-green-600 border-gray-300 rounded focus:ring-green-500"
                                    />
                                    <span className="text-sm text-gray-700">{t('manager.modules.adminBins.binType.create.autoReplenishment', {}, 'Automatisk påfylling')}</span>
                                </label>
                                <label className="flex items-center gap-2 cursor-pointer">
                                    <input
                                        type="checkbox"
                                        checked={formData.sharedBinYesNo ?? false}
                                        onChange={(e) => updateField('sharedBinYesNo', e.target.checked)}
                                        className="w-4 h-4 text-green-600 border-gray-300 rounded focus:ring-green-500"
                                    />
                                    <span className="text-sm text-gray-700">{t('manager.modules.adminBins.binType.fields.sharedBin', {}, 'Felleslokasjon')}</span>
                                </label>
                                <label className="flex items-center gap-2 cursor-pointer">
                                    <input
                                        type="checkbox"
                                        checked={formData.sharedBinPreferEmptyBin ?? false}
                                        onChange={(e) => updateField('sharedBinPreferEmptyBin', e.target.checked)}
                                        className="w-4 h-4 text-green-600 border-gray-300 rounded focus:ring-green-500"
                                    />
                                    <span className="text-sm text-gray-700">{t('manager.modules.adminBins.binType.fields.preferEmptySharedBin', {}, 'Prioriter tomme felleslok.')}</span>
                                </label>
                                <div>
                                    <label className="block text-xs font-medium text-gray-700 mb-1">{t('manager.modules.adminBins.binType.create.maxQtySharedBin', {}, 'Maks ant. på felleslok.')}</label>
                                    <input
                                        type="number"
                                        value={formData.sharedBinMaxQuantity ?? ''}
                                        onChange={(e) => updateField('sharedBinMaxQuantity', e.target.value ? parseInt(e.target.value) : undefined)}
                                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                                        placeholder="0"
                                    />
                                </div>
                            </div>
                        </fieldset>

                        {/* Etterfylling */}
                        <fieldset className="border border-gray-200 rounded-lg p-4">
                            <legend className="text-sm font-semibold text-gray-700 px-2">{t('manager.modules.adminBins.binType.replenishment', {}, 'Etterfylling')}</legend>
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-xs font-medium text-gray-700 mb-1">{t('manager.modules.adminBins.binType.create.minLevel', {}, 'Min. nivå')}</label>
                                    <input
                                        type="number"
                                        value={formData.replenishMinLevel ?? ''}
                                        onChange={(e) => updateField('replenishMinLevel', e.target.value ? parseInt(e.target.value) : undefined)}
                                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                                        placeholder="0"
                                    />
                                </div>
                                <div>
                                    <label className="block text-xs font-medium text-gray-700 mb-1">{t('manager.modules.adminBins.binType.create.maxLevel', {}, 'Maks. nivå')}</label>
                                    <input
                                        type="number"
                                        value={formData.replenishMaxLevel ?? ''}
                                        onChange={(e) => updateField('replenishMaxLevel', e.target.value ? parseInt(e.target.value) : undefined)}
                                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                                        placeholder="0"
                                    />
                                </div>
                                <div>
                                    <label className="block text-xs font-medium text-gray-700 mb-1">{t('manager.modules.adminBins.binType.fields.maxReplenishBins', {}, 'Maks påfyllingslok.')}</label>
                                    <input
                                        type="number"
                                        value={formData.replenishMaxBins ?? ''}
                                        onChange={(e) => updateField('replenishMaxBins', e.target.value ? parseInt(e.target.value) : undefined)}
                                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                                        placeholder="0"
                                    />
                                </div>
                                <div>
                                    <label className="block text-xs font-medium text-gray-700 mb-1">{t('manager.modules.adminBins.binType.fields.maxSourceBins', {}, 'Maks kildelok.')}</label>
                                    <input
                                        type="number"
                                        value={formData.replenishMaxBinsSource ?? ''}
                                        onChange={(e) => updateField('replenishMaxBinsSource', e.target.value ? parseInt(e.target.value) : undefined)}
                                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                                        placeholder="0"
                                    />
                                </div>
                                <label className="flex items-center gap-2 cursor-pointer">
                                    <input
                                        type="checkbox"
                                        checked={formData.replenishFixedBins ?? false}
                                        onChange={(e) => updateField('replenishFixedBins', e.target.checked)}
                                        className="w-4 h-4 text-green-600 border-gray-300 rounded focus:ring-green-500"
                                    />
                                    <span className="text-sm text-gray-700">{t('manager.modules.adminBins.binType.fields.fixedReplenishBins', {}, 'Faste påfyllingslok.')}</span>
                                </label>
                            </div>
                        </fieldset>

                        {/* Footer */}
                        <div className="flex items-center justify-end gap-3 pt-4 border-t border-gray-200">
                            <button
                                type="button"
                                onClick={onClose}
                                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                            >
                                {t('common.buttons.cancel', {}, 'Avbryt')}
                            </button>
                            <button
                                type="submit"
                                disabled={isSaving}
                                className={`flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
                                    isSaving
                                        ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                                        : 'bg-green-600 text-white hover:bg-green-700'
                                }`}
                            >
                                {isSaving ? (
                                    <>
                                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                                        {t('manager.modules.adminBins.common.creating', {}, 'Oppretter...')}
                                    </>
                                ) : (
                                    <>
                                        <Plus className="w-4 h-4" />
                                        {t('manager.modules.adminBins.binType.create.submit', {}, 'Opprett lokasjonstype')}
                                    </>
                                )}
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
};
